export const MAX_DIMENSION = 3500

interface ResizeImageFileToPngOptionsI {
  fillBackground?: boolean
}

/**
 * Loads an image file onto a canvas, scales it so the largest dimension is
 * MAX_DIMENSION (3500px) while maintaining aspect ratio, and returns a PNG Blob.
 */
export const resizeImageFileToPng = async (
  file: File,
  options?: ResizeImageFileToPngOptionsI
): Promise<Blob> => {
  const objectUrl = URL.createObjectURL(file)
  const img = new Image()

  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('Failed to load image for resizing'))
      img.src = objectUrl
    })
  } finally {
    URL.revokeObjectURL(objectUrl)
    img.onload = null
    img.onerror = null
  }

  const largestDimension = Math.max(img.naturalWidth, img.naturalHeight)
  if (largestDimension <= 0) {
    throw new Error('Image has invalid dimensions (0x0)')
  }
  const scale = MAX_DIMENSION / largestDimension
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth * scale
  canvas.height = img.naturalHeight * scale

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('Failed to get canvas 2D context')
  }

  if (options?.fillBackground) {
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)
  }

  context.drawImage(img, 0, 0, canvas.width, canvas.height)

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to convert canvas to blob'))
        }
      },
      'image/png',
      1.0
    )
  })
}

/**
 * Converts a JPG/JPEG file to a PNG image file.
 * The resulting image is scaled so the largest dimension is MAX_DIMENSION (3500px)
 * while maintaining the original aspect ratio.
 */
export const convertJpgToPng = async (jpgFile: File): Promise<File> => {
  const blob = await resizeImageFileToPng(jpgFile, { fillBackground: true })
  const baseName = jpgFile.name.replace(/\.jpe?g$/i, '')
  return new File([blob], `${baseName}.png`, { type: 'image/png' })
}

/**
 * Resizes a PNG file so the largest dimension is MAX_DIMENSION (3500px)
 * while maintaining the original aspect ratio. Transparency is preserved.
 */
export const resizePng = async (pngFile: File): Promise<File> => {
  const blob = await resizeImageFileToPng(pngFile)
  const baseName = pngFile.name.replace(/\.png$/i, '')
  return new File([blob], `${baseName}.png`, { type: 'image/png' })
}
